import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  RedoOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { ClipboardText, SlidersHorizontal } from '@phosphor-icons/react';

import RecordsHOC from '../RecordsHOC';
import { Header } from '../../../components';
import { sentColumns } from '../shared/constants';
import CannotAccess from '../../CannotAccess';

import {
  getSentRecordsByPage,
  getSentByQuery,
  resendAllFailedFaxes,
} from '../../../actions/records';
import { openModal } from '../../../actions/modal';
import { captureAnalytics } from '../../../actions/preload';
import {
  verifyFeatureAccess,
  verifySentInboxAccess,
  verifySentInboxOwnRecordsOnly,
} from '../../../global/featureFlags';
import ChartActionsMenu from '../../../components/chartActions/ChartActionsMenu';
import { handleSendToChart, handleCreateChart, normalizeRecord } from '../../../components/chartActions/helpers';
import { shouldUseLegacyEmrActions } from '../../../components/chartActions/credentialCapabilities';
import { hasPatientRecordQueryParam } from '../../../util/patientRecordUrl';

// The filter options that will appear in the filter drop down menu list
const filterOptions = [
  { filter: 'all', text: <span>Records Sent to <em>Providers</em></span> },
  { filter: 'fax', text: <span>Faxes Sent to <em>Providers</em></span> },
  { filter: 'with_referral_reply', text: <span>Referral Replies Sent to <em>Providers</em></span>, badge: 'New' },
  { filter: 'email', text: <span>Emails Sent to <em>Providers</em></span> },
  { filter: 'patients', text: <span>Emails or Text Messages Sent to <em>Patients</em></span> },
];

export class SentRecords extends Component {
  static propTypes = {
    openModal: PropTypes.func.isRequired,
    isResendingUnviewed: PropTypes.bool,
    isResendingFailed: PropTypes.bool,
    totalRecordsResent: PropTypes.number,
    location: PropTypes.object.isRequired,
    resendAllFailedFaxes: PropTypes.func.isRequired,
    getSentByQuery: PropTypes.func.isRequired,
    hasEmrIntegrationCredential: PropTypes.bool.isRequired,
    autoUploadEmr: PropTypes.string,
    currentOrganizationId: PropTypes.string.isRequired,
    // Injected by RecordsHOC to open the schema-driven three-layer EHR drawer.
    onOpenEhrDrawer: PropTypes.func.isRequired,

    /* Data selected on inbox page */
    selectedData: PropTypes.shape({
      file_token: PropTypes.string,
      patient_dob: PropTypes.string,
      patient_name: PropTypes.string,
      upload_date: PropTypes.string,
    }),
  }

  static defaultProps = {
    selectedData: {},
    isResendingUnviewed: false,
    isResendingFailed: false,
    totalRecordsResent: null,
    autoUploadEmr: '',
  }

  constructor(props) {
    super(props);
    this.state = {
      openMenu: false,
      disableSingleResend: true,
      filterValue: filterOptions[0],
      filterOptions: filterOptions.slice(1), // Removes the first option from the list
      openFilterMenu: false,
      canResendAllFailed: false,
      hasSentInboxAccess: null, // null = checking, true = has access, false = no access
      ownRecordsOnly: false, // ENG-3525: role locks this user to the records they sent
      createPatientChart: false, // gates three-layer Create Chart in the Chart Actions dropdown
    };
  }

  componentDidMount = async () => {
    // Use async helper to prevent race conditions (same fix as ReceivedRecords).
    // The three checks are independent, so run them concurrently rather than
    // making the page wait for three serial round-trips.
    const [hasAccess, canResendAllFailed, createPatientChart] = await Promise.all([
      verifySentInboxAccess(),
      verifyFeatureAccess('resend_failed_records'),
      verifyFeatureAccess('create_patient_chart'),
    ]);
    const ownRecordsOnly = verifySentInboxOwnRecordsOnly();

    this.setState({
      hasSentInboxAccess: hasAccess,
      canResendAllFailed,
      createPatientChart,
      ownRecordsOnly,
    });
    // Non-PHI: records which users land on the restricted view (app/javascript/src/AGENTS.md).
    if (hasAccess && ownRecordsOnly) captureAnalytics('sent_inbox_own_records_only_viewed');

    let recordsFilterIndex = 0;
    const { search } = this.props.location;
    const searchParams = new URLSearchParams(search);
    filterOptions.forEach(({ filter }, index) => {
      if (searchParams.has(filter)) recordsFilterIndex = index;
    });
    this.updateFilterMenu(recordsFilterIndex);
  }

  componentWillReceiveProps(nextProps) {
    // Close drop down menu when a record is selected or unselected
    if ((this.state.openMenu || this.state.openFilterMenu) && (nextProps.selectedData !== this.props.selectedData)) {
      this.setState({
        openMenu: false,
        openFilterMenu: false,
      });
    }
    // Check if resending is successful and display different toastr msgs for bulk
    // and single resend.
    if (!nextProps.isResendingUnviewed && this.props.isResendingUnviewed) {
      /* global toastr */
      let toastrMessage = 'The record has been resent successfully';
      if (this.state.disableSingleResend) {
        if (nextProps.totalRecordsResent === 0) {
          const { batchSize } = this.state;
          toastrMessage = `There are no unviewed records in the past ${batchSize} days to be resent.`;
          return toastr.info(toastrMessage);
        }
        if (nextProps.totalRecordsResent === 1) {
          toastrMessage = '1 record has been resent successfully';
        }
        if (nextProps.totalRecordsResent > 1) {
          toastrMessage = `${nextProps.totalRecordsResent} records have been resent successfully`;
        }
      }
      toastr.success(toastrMessage);
    }
    // Do the same, but for failed records. TODO(jack) Redundant code.
    if (!nextProps.isResendingFailed && this.props.isResendingFailed) {
      /* global toastr */
      let toastrMessage = 'The record has been resent successfully';
      if (this.state.disableSingleResend) {
        if (nextProps.totalRecordsResent === 0) {
          toastrMessage = 'There are no failed records to be resent.';
          return toastr.info(toastrMessage);
        }
        if (nextProps.totalRecordsResent === 1) {
          toastrMessage = '1 record has been resent successfully';
        }
        if (nextProps.totalRecordsResent > 1) {
          toastrMessage = `${nextProps.totalRecordsResent} records have been resent successfully`;
        }
      }
      toastr.success(toastrMessage);
    }
  }

  handleDropDown = async (visible) => {
    if (!visible) {
      this.setState({ openMenu: false });
      return;
    }

    const canResendRecord = await verifyFeatureAccess('resend_records');
    if (!canResendRecord) {
      const modalType = {
        type: 'RAISE_FLAG',
        featureFlag: 'resend_records',
      };
      return this.props.openModal(modalType);
    }
    const { selectedData } = this.props;
    this.setState({
      openMenu: true,
      disableSingleResend: Object.keys(selectedData).length === 0,
    });
  }

  // If the page is refreshed and there was an active filter, this will ensure that the
  // filter values remain consistent
  updateFilterMenu = (index) => {
    this.setState({
      filterValue: filterOptions[index],
      filterOptions: filterOptions.filter(option => option !== filterOptions[index]),
    });
  }

  handleResendRecord = ({ key }) => {
    const value = key;
    const data = value === '1' ? { recipient: this.props.selectedData } : { days: value };
    const modalType = {
      type: 'RESEND_RECORD',
      data,
    };
    this.setState({
      batchSize: value,
      openMenu: false,
    });
    this.props.openModal(modalType);
  }

  // Fetches the correct list of records according to the filter selected.
  // Also updates the filter drop down menu list to show current active filter
  handleFilter = ({ key }) => {
    const selectedFilterKey = key;
    const filterQuery = filterOptions.findIndex(({ filter }) => filter === selectedFilterKey);
    const selectedIndex = filterQuery >= 0 ? filterQuery : 0;

    const { filter: selectedFilter } = filterOptions[selectedIndex];
    this.props.getSentByQuery(`?${selectedFilter}=true`);
    this.setState({
      filterValue: filterOptions[selectedIndex],
      filterOptions: filterOptions.filter(({ filter }) => filter !== selectedFilter),
      openFilterMenu: false,
    });
  }

  /**
   * Handles resending of all records that failed during transmission.
   * This records were sent by fax
   */
  handleResendAllFaxes = async () => {
    const canResendAllFaxes = await verifyFeatureAccess('resend_failed_records');
    if (canResendAllFaxes) return this.props.resendAllFailedFaxes();
    const modalType = {
      type: 'RAISE_FLAG',
      featureFlag: 'resend_failed_records',
    };
    this.props.openModal(modalType);
  }

  buildSelectedRecord = (record) => {
    if (!record) {
      return null;
    }

    return normalizeRecord(record, {
      // get_sent_object now serializes parent_type: 'Receiver' (ENG-3009); before that
      // it emitted no parent_type at all, so this was undefined for every real Sent row
      // and /api/v1/chart_uploads rejected the submission with 'parent_type is required'.
      // The fallback stays as a belt-and-braces default: every row this page renders is
      // a Receiver, so there is no case where guessing is wrong.
      parentType: record.parent_type || 'Receiver',
      dynamicSendToken: record.send_token,
      uploadReceiverType: record.receiver_type,
      isEmailAttachment: record.receiver_type === 'email_attachment',
      isEmailSubmission: record.is_email_submission === true,
      isSentContext: true,
    });
  };

  autoUploadRecord = async (selectedRecord) => {
    const {
      patient_name: patientName,
      patient_dob: patientDOB,
      send_token: sendToken,
      receiver_type: receiverType,
    } = selectedRecord;
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    // If EMR is EMA, we will allow Send Chart Modal to open as Patient MRN is an option to send with
    // chart instead of Patient Name and DOB.
    if (!patientName || !patientDOB) {
      message.error({
        content: 'Cannot upload a document without a valid name and date of birth. Edit record details and try again.',
      });
    } else {
      let recordFileName = selectedRecord.document_title;
      if (!recordFileName) {
        recordFileName = `${selectedRecord.created_at.replace(/ /g, '_')}_${selectedRecord.sender_name.replace(/ /g, '_')}`;
      }
      const modalType = {
        type: 'AUTO_UPLOAD',
        data: {
          fileName: recordFileName,
          providerName: selectedRecord.practitioner_name,
          dateOfService: selectedRecord.date_of_service,
          patientName: selectedRecord.patient_name,
          patientFirstName: selectedRecord.patient_first_name,
          patientMiddleName: selectedRecord.patient_middle_name,
          patientLastName: selectedRecord.patient_last_name,
          patientDOB: selectedRecord.patient_dob,
          documentCategory: selectedRecord.document_category,
          sendToken,
          receiverType,
          autoUploadEmr,
          noTaskCreation: true,
          currentOrganizationId: this.props.currentOrganizationId,
        },
      };
      this.props.openModal(modalType);
    }
  }

  handleDropdownSendToChart = (selectedRecord) => {
    const record = this.buildSelectedRecord(selectedRecord);
    handleSendToChart({
      record,
      dependencies: {
        onOpenEhrDrawer: this.props.onOpenEhrDrawer,
      },
    });
  };

  handleDropdownCreateChart = (selectedRecord) => {
    const record = this.buildSelectedRecord(selectedRecord);
    // Non-PHI workflow/source metadata only (app/javascript/src/AGENTS.md).
    captureAnalytics('chart_action_clicked', {
      action: 'create_chart',
      source: 'sent_page',
      parent_type: record?.parentType,
    });
    handleCreateChart({
      record,
      dependencies: {
        onOpenEhrDrawer: this.props.onOpenEhrDrawer,
      },
    });
  };

  render() {
    // Show loading while checking access (same pattern as Analytics)
    if (this.state.hasSentInboxAccess === null) {
      return <div>Loading...</div>;
    }

    // Show CannotAccess if user doesn't have permission (same pattern as Analytics)
    if (!this.state.hasSentInboxAccess) {
      return <CannotAccess />;
    }

    const {
      disableSingleResend,
      openMenu,
      openFilterMenu,
      canResendAllFailed,
      ownRecordsOnly,
    } = this.state;
    const {
      selectedData,
    } = this.props;
    const hasSelectedRecord = selectedData && Object.keys(selectedData).length > 0;
    const chartDropdownDisabled = !hasSelectedRecord;
    const selectedRecord = hasSelectedRecord ? selectedData : null;
    const isPatientRecordsPage = hasPatientRecordQueryParam(this.props.location.search);
    const useLegacyEmrActions = shouldUseLegacyEmrActions({
      hasEmrIntegrationCredential: this.props.hasEmrIntegrationCredential,
      autoUploadEmr: this.props.autoUploadEmr,
    });

    return (
      <div>
        <Helmet>
          <title>Sent Records - Medsender</title>
        </Helmet>
        <Header
          main={'Sent records'}
          subtext={ownRecordsOnly ? 'Showing records you sent' : 'Choose a record below'}
          locationPath={[
            { icon: 'home', text: 'Home', link: '/app' },
            { icon: 'inbox', text: 'Sent', link: '/app/sent' },
          ]}
          buttons={[
            {
              disableSingleResend,
              openMenu,
              hide: isPatientRecordsPage,
              text: 'Resend Records',
              icon: <RedoOutlined />,
              resendRecords: true,
              disabled: false,
              handleResendRecord: this.handleResendRecord,
              handleRequestChange: this.handleDropDown,
            },
            {
              text: 'Resend All Failed Records',
              icon: <SendOutlined />,
              disabled: false,
              hide: !canResendAllFailed,
              func: this.handleResendAllFaxes,
            },
            {
              openFilterMenu,
              text: <span>Viewing {this.state.filterValue.text}</span>,
              icon: <SlidersHorizontal size={18} />,
              filterRecords: true,
              disabled: false,
              showBadge: !openFilterMenu,
              handleFilter: this.handleFilter,
              filterOptions: this.state.filterOptions,
              handleRequestChange: value => this.setState({ openFilterMenu: value }),
            },
            {
              type: 'dropdown',
              text: 'Chart Actions',
              icon: <ClipboardText size={18} weight="fill" />,
              disabled: chartDropdownDisabled,
              hide: !this.props.hasEmrIntegrationCredential || isPatientRecordsPage,
              menu: (
                <ChartActionsMenu
                  canSendToChart
                  canCreateChart={this.state.createPatientChart}
                  onSendToChart={() => selectedRecord && this.handleDropdownSendToChart(selectedRecord)}
                  onCreateChart={() => selectedRecord && this.handleDropdownCreateChart(selectedRecord)}
                />
              ),
            },
            {
              text: 'Send to Chart',
              icon: <ClipboardText size={18} weight="fill" />,
              disabled: Object.keys(selectedData).length < 1 || localStorage.m8_org === 'BCP Group',
              hide: !useLegacyEmrActions || isPatientRecordsPage,
              func: () => this.autoUploadRecord(selectedData),
            },
          ]}
        />
      </div>
    );
  }
}

const passedProps = {
  getSelectedPageData: getSentRecordsByPage,
  getPageDataWithQuery: getSentByQuery,
  data: 'sent_data',
  mode: 'Sent',
  columnTitles: sentColumns,
  resendAllFailedFaxes,
};

export default RecordsHOC(passedProps)(
  connect(state => ({
    selectedData: state.inboxTable.recordData[0],
    isResendingUnviewed: state.records.isResendingUnviewed,
    isResendingFailed: state.records.isResendingFailed,
    totalRecordsResent: state.records.totalRecordsResent,
    hasEmrIntegrationCredential: state.auth.hasEmrIntegrationCredential,
    autoUploadEmr: state.auth.data.autoUploadEmr,
    currentOrganizationId: state.auth.currentOrganizationId,
  }), {
    openModal,
    resendAllFailedFaxes,
    getSentByQuery,
  })(SentRecords),
);
